// client/src/components/templates/recibos/TemplateReciboForm.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMutateTemplateRecibo, useReciboPlaceholders } from '../../../hooks/useTemplateRecibo';
import useAlert from '../../../hooks/useAlert';
import { useAuth } from '../../../hooks/useAuth';
import { isMatrizAdmin as checkIsMatrizAdmin } from '../../../utils/permissions';
import { Save, XCircle, Loader2, Code, Eye, ClipboardCopy, ChevronDown } from 'lucide-react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import PropTypes from 'prop-types';
import PlaceholdersPanel from '../PlaceholdersPanel';

const TemplateReciboForm = ({ onCloseForm, current }) => {
    const { user } = useAuth();
    const { setAlert } = useAlert();
    const { addTemplateRecibo, updateTemplateRecibo, isPendingRecibo: isMutating } = useMutateTemplateRecibo();
    const { data: placeholders, isLoading: isLoadingPlaceholders } = useReciboPlaceholders();

    const isMatrizAdmin = checkIsMatrizAdmin(user) && !user?.account && !user?.franquia;

    const initialState = { nome: '', descricao: '', conteudo: '', ativo: true, isPadrao: false };

    const [template, setTemplate] = useState(initialState);
    const [viewMode, setViewMode] = useState('editor');
    const [showVariables, setShowVariables] = useState(false);
    const editorInstance = useRef(null);

    useEffect(() => {
        if (current) {
            setTemplate({
                _id: current._id,
                nome: current.nome || '',
                descricao: current.descricao || '',
                conteudo: current.conteudo || '',
                ativo: current.ativo !== undefined ? current.ativo : true,
                isPadrao: current.isPadrao || false,
            });
        } else {
            setTemplate(initialState);
        }
    }, [current]);

    const onEditorReady = useCallback(editor => {
        editorInstance.current = editor;
        if (!current && editor.getData() === '') {
            const defaultContent = `<p>Recibo referente aos serviços prestados...</p>`;
            editor.setData(defaultContent);
            setTemplate(prev => ({ ...prev, conteudo: defaultContent }));
        }
    }, [current]);

    const onChange = e => {
        const { name, value, type, checked } = e.target;
        setTemplate(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };
    
    const onEditorChange = (e, editor) => {
        setTemplate(prev => ({ ...prev, conteudo: editor.getData() }));
    };

    const onSubmit = async e => {
        e.preventDefault();
        const payload = { ...template };
        if (!isMatrizAdmin) {
            delete payload.isPadrao;
        }

        try {
            if (current) {
                await updateTemplateRecibo.mutateAsync({ id: template._id, templateData: payload });
            } else {
                await addTemplateRecibo.mutateAsync(payload);
            }
            onCloseForm();
        } catch (error) {
            console.error("Erro ao salvar template de recibo:", error);
        }
    };

    const insertPlaceholder = useCallback((text) => {
        const editor = editorInstance.current;
        if (editor) {
            editor.model.change(writer => {
                const insertPosition = editor.model.document.selection.getFirstPosition();
                writer.insertText(text, insertPosition);
            });
            editor.editing.view.focus();
            setShowVariables(false);
        }
    }, []);

    return (
        <form onSubmit={onSubmit} noValidate>
            <div className="form-header">
                <h3 className="form-title">{current ? `Editar Template: ${template.nome}` : 'Novo Template de Recibo'}</h3>
                <button type="button" onClick={onCloseForm} title="Fechar" className="form-close-button"><XCircle size={24} /></button>
            </div>

            <fieldset className="form-section">
                <legend className="form-section-title">Informações Gerais</legend>
                <div className="form-group">
                    <label htmlFor="nome">Nome do Template*</label>
                    <input type="text" id="nome" name="nome" value={template.nome} onChange={onChange} required className="form-input" />
                </div>
                <div className="form-group">
                    <label htmlFor="descricao">Descrição</label>
                    <input type="text" id="descricao" name="descricao" value={template.descricao} onChange={onChange} className="form-input" />
                </div>
            </fieldset>

            <fieldset className="form-section">
                <legend className="form-section-title">Conteúdo do Recibo*</legend>
                <div className="editor-toolbar">
                    <button type="button" onClick={() => setShowVariables(!showVariables)} className="action-button action-button-secondary">
                        <ClipboardCopy size={16} className="icon" /> Inserir Variável <ChevronDown size={16} className={`transition-transform ${showVariables ? 'rotate-180' : ''}`} />
                    </button>
                    <button type="button" onClick={() => setViewMode(viewMode === 'preview' ? 'editor' : 'preview')} className="action-button action-button-secondary">
                        <Eye size={16} className="icon" /> {viewMode === 'preview' ? 'Voltar ao Editor' : 'Preview'}
                    </button>
                    <button type="button" onClick={() => setViewMode(viewMode === 'source' ? 'editor' : 'source')} className="action-button action-button-secondary">
                        <Code size={16} className="icon" /> {viewMode === 'source' ? 'Editor Visual' : 'Código HTML'}
                    </button>
                </div>
                {showVariables && <PlaceholdersPanel placeholders={placeholders} isLoading={isLoadingPlaceholders} onInsert={insertPlaceholder} />}

                <div className="editor-container">
                    {viewMode === 'editor' && (
                        <CKEditor
                            editor={ClassicEditor}
                            data={template.conteudo} 
                            onChange={onEditorChange}
                            onReady={onEditorReady}
                            key={current ? current._id : 'new-template-recibo'} 
                        />
                    )}
                    {viewMode === 'preview' && <div className="editor-preview prose max-w-none" dangerouslySetInnerHTML={{ __html: template.conteudo }}></div>}
                    {viewMode === 'source' && <textarea name="conteudo" value={template.conteudo} onChange={onChange} rows="15" className="editor-source-textarea"></textarea>}
                </div>
            </fieldset>

            <fieldset className="form-section">
                <legend className="form-section-title">Configurações</legend>
                <div className="form-group-checkbox">
                    <input type="checkbox" name="ativo" id="recibo-ativo" checked={template.ativo} onChange={onChange} className="form-checkbox" />
                    <label htmlFor="recibo-ativo">Template ativo</label>
                </div>
                {isMatrizAdmin && (
                    <div className="form-group-checkbox mt-4">
                        <input type="checkbox" name="isPadrao" id="recibo-isPadrao" checked={template.isPadrao} onChange={onChange} className="form-checkbox" />
                        <label htmlFor="recibo-isPadrao">Template Padrão Global</label>
                    </div>
                )}
            </fieldset>

            <div className="form-actions">
                <button type="button" onClick={onCloseForm} className="action-button action-button-secondary"><XCircle size={18} className="icon" />Cancelar</button>
                <button type="submit" disabled={isMutating} className="action-button action-button-primary">
                    {isMutating ? <Loader2 className="animate-spin icon" /> : <Save size={18} className="icon" />}
                    {current ? 'Atualizar Template' : 'Salvar Template'}
                </button>
            </div>
        </form>
    );
};

TemplateReciboForm.propTypes = {
    onCloseForm: PropTypes.func.isRequired,
    current: PropTypes.object,
};

TemplateReciboForm.defaultProps = {
    current: null,
};

export default TemplateReciboForm;